import { useRef, useState } from 'react';

/**
 * Reusable bulk-import modal for CSV / XLSX uploads.
 *
 * Props:
 *   open, onClose
 *   title            — e.g. "Import leads"
 *   columns          — array of { key, required?, help? } for the template + hints
 *   sampleRow        — array of example cell values matching `columns` order
 *   templateName     — download filename, e.g. "leads-template.csv"
 *   onImport(file)   — returns a Promise resolving to an ImportResult
 *                      { total, created, updated, skipped, errors:[{row,message}] }
 *   onDone(result)   — called after a successful import (e.g. to refresh a list)
 */
export default function BulkImportModal({
  open, onClose, title, columns = [], sampleRow = [], templateName = 'template.csv', onImport, onDone,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  if (!open) return null;

  const reset = () => { setFile(null); setResult(null); setError(null); setBusy(false); };
  const close = () => { reset(); onClose?.(); };

  const csvEscape = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadTemplate = () => {
    const header = columns.map((c) => c.key).join(',');
    const sample = (sampleRow.length ? sampleRow : columns.map(() => '')).map(csvEscape).join(',');
    const blob = new Blob([`${header}\n${sample}\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = templateName; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await onImport(file);
      setResult(res);
      onDone?.(res);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={close}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); submit(); }}
            style={{ width: 'min(620px, 94vw)', maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-header">
          <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
          <button type="button" className="modal-close" onClick={close} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!result && (
            <>
              <div className="text-xs text-muted">
                Upload a <b>.csv</b> or <b>.xlsx</b> file. The first row must be column headers — matching is
                case- and spacing-insensitive. Unknown columns are ignored; bad rows are skipped and reported.
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="btn small" onClick={downloadTemplate}>⭳ Download template</button>
                <span className="text-xs text-muted">Fields: {columns.map((c) => c.key + (c.required ? '*' : '')).join(', ')}</span>
              </div>

              <div
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
                style={{
                  border: '1.5px dashed var(--border, #cbd2d9)', borderRadius: 8, padding: 24,
                  textAlign: 'center', cursor: 'pointer', background: 'var(--surface-2, #fafbfc)',
                }}
              >
                <div style={{ fontSize: 26 }}>⭱</div>
                <div style={{ fontWeight: 600 }}>{file ? file.name : 'Click to choose a file, or drop it here'}</div>
                <div className="text-xs text-muted">CSV, XLSX or XLS</div>
                <input ref={inputRef} type="file" accept=".csv,.tsv,.xlsx,.xls" style={{ display: 'none' }}
                       onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </div>

              {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
            </>
          )}

          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Stat label="Rows" value={result.total} />
                <Stat label="Created" value={result.created} tone="success" />
                {result.updated > 0 && <Stat label="Updated" value={result.updated} tone="accent" />}
                <Stat label="Skipped" value={result.skipped} tone={result.skipped ? 'danger' : ''} />
              </div>
              {result.errors?.length > 0 && (
                <div className="card" style={{ padding: 0, maxHeight: 220, overflow: 'auto' }}>
                  <table className="data-table" style={{ width: '100%' }}>
                    <thead><tr><th style={{ width: 70 }}>Row</th><th>Reason skipped</th></tr></thead>
                    <tbody>
                      {result.errors.map((e, i) => (
                        <tr key={i}><td className="mono">{e.row}</td><td className="text-xs">{e.message}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!result.errors?.length && <div style={{ color: 'var(--success)', fontSize: 13 }}>All rows imported cleanly. 🎉</div>}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!result ? (
            <>
              <button type="button" className="btn small" onClick={close}>Cancel</button>
              <button type="submit" className="btn small primary" disabled={!file || busy}>
                {busy ? 'Importing…' : 'Import'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn small" onClick={reset}>Import another</button>
              <button type="button" className="btn small primary" onClick={close}>Done</button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === 'success' ? 'var(--success)' : tone === 'danger' ? 'var(--danger)'
    : tone === 'accent' ? 'var(--accent-primary)' : 'var(--text-primary)';
  return (
    <div className="card" style={{ padding: '8px 14px', minWidth: 90 }}>
      <div className="card-label">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value ?? 0}</div>
    </div>
  );
}
